import { useReducer } from 'react';
import {
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import { aggregatedStoreListSelector } from 'store/selectors/locationServicesSelector';
import { useAppSelector } from 'store/store';
import { AggregatedStoreData } from 'types';
import { SkuDetails } from '../analytics/types';
import { StyleDetails } from '../components/FulfillmentOptions/types';

type InitProps = {
  skus: SkuDetails[];
  selected: Record<string, string>;
  enabled: Record<string, string[]> | undefined;
  justClicked: string;
  resolved: SkuDetails[];
  initialEnabled: Record<string, string[]> | undefined;
  quickviewAutoSelectEnabled?: boolean;
  nearbyStores?: Record<string, AggregatedStoreData[]> | null;
};

type UseAttributeEngineProps = {
  style: StyleDetails;
  skus: SkuDetails[];
};

// check if SKU has any inventory available before building options
// Matches PDP that checks shipQty, bopis, isaQty and nearby stores
const hasInventory = (
  sku: SkuDetails,
  nearbyStores?: Record<string, AggregatedStoreData[]> | null,
): boolean => {
  const hasEcommInventory = sku.shipQty !== undefined && sku.shipQty > 0;
  const hasBopisInventory =
    (sku.bopisQty !== undefined && sku.bopisQty > 0) ||
    (sku.isaQty !== undefined && sku.isaQty > 0);
  const hasNearbyStores = !!(
    nearbyStores &&
    sku.partNumber &&
    nearbyStores[sku.partNumber]?.length > 0
  );

  return hasEcommInventory || hasBopisInventory || hasNearbyStores;
};

const filterResolvedSku = (skus: SkuDetails[], selected: Record<string, string>) => {
  return skus.filter((sku: SkuDetails) => {
    let invalidAttributes = [];
    if (sku.definingAttributes) {
      // checking each defining attribute against selections
      invalidAttributes = sku.definingAttributes.filter((attr) => {
        const selectedValue = selected[attr.name];
        if (selectedValue && attr.value !== selectedValue) {
          return true;
        }
        return false;
      });
    }

    // if there is inventory and no defining attributes conflict this sku is valid
    return invalidAttributes.length <= 0;
  });
};

// filter SKUs matching all selected attributes
const filterSkusMatchingSelections = (
  skus: SkuDetails[],
  selected: Record<string, string>,
): SkuDetails[] => {
  if (!selected || Object.keys(selected).length === 0) {
    return skus;
  }

  return skus.filter((sku) => {
    if (!sku.definingAttributes) return false;

    return Object.entries(selected).every(([attrName, attrValue]) => {
      return sku.definingAttributes.some(
        (attr) =>
          attr.name.toLowerCase() === attrName.toLowerCase() &&
          attr.value.toLowerCase() === attrValue.toLowerCase(),
      );
    });
  });
};

// build available attribute options from filtered SKUs (matches PDP's getAvailableAttributeOptionsForSelectedAttribute)
// excludeAttribute allow switching between values in the same attribute
const getAvailableAttributeOptions = (
  filteredSkus: SkuDetails[],
  excludeAttribute?: string,
  nearbyStores?: Record<string, AggregatedStoreData[]> | null,
): Record<string, string[]> => {
  const availableOptions: Record<string, string[]> = {};

  // filter out SKUs with no inventory (checks ecomm, bopis, and nearby stores)
  const skusWithInventory = filteredSkus.filter((sku) => hasInventory(sku, nearbyStores));

  skusWithInventory.forEach((sku) => {
    if (!sku.definingAttributes) return;

    sku.definingAttributes.forEach((attr) => {
      if (excludeAttribute && attr.name.toLowerCase() === excludeAttribute.toLowerCase()) {
        return;
      }

      if (!availableOptions[attr.name]) {
        availableOptions[attr.name] = [];
      }

      if (
        !availableOptions[attr.name].some((val) => val.toLowerCase() === attr.value.toLowerCase())
      ) {
        availableOptions[attr.name].push(attr.value);
      }
    });
  });

  return availableOptions;
};

// remove selections that are no longer available
const filterIncompatibleSelections = (
  currentSelected: Record<string, string>,
  availableOptions: Record<string, string[]>,
  justClicked: string,
): Record<string, string> => {
  const newSelected: Record<string, string> = {};

  Object.entries(currentSelected).forEach(([attrName, attrValue]) => {
    // keep if it's the just clicked attribute or exists in available options
    if (
      attrName === justClicked ||
      availableOptions[attrName]?.some((val) => val.toLowerCase() === attrValue.toLowerCase())
    ) {
      newSelected[attrName] = attrValue;
    }
  });

  return newSelected;
};

function init({
  skus,
  selected,
  enabled,
  justClicked,
  initialEnabled,
  quickviewAutoSelectEnabled,
  nearbyStores,
}: InitProps) {
  // filter SKUs matching all selected attributes
  const matchedSkus = filterSkusMatchingSelections(skus, selected);

  // build available options from matched SKUs, excluding justClicked attribute
  const availableOptions = getAvailableAttributeOptions(matchedSkus, justClicked, nearbyStores);

  // remove incompatible selections
  let newSelected = justClicked
    ? filterIncompatibleSelections(selected, availableOptions, justClicked)
    : { ...selected };

  // make auto selections based on a singular resolved sku
  const newResolved = filterResolvedSku(skus, newSelected);
  if (newResolved.length === 1 && quickviewAutoSelectEnabled) {
    if (newResolved[0].definingAttributes) {
      newResolved[0].definingAttributes.forEach((attr) => {
        newSelected = {
          ...newSelected,
          [attr.name]: attr.value,
        };
      });
    }
  }

  // build enabled list for unselected attributes, preserve initial state for selected ones
  const finalMatchedSkus = filterSkusMatchingSelections(skus, newSelected);
  let newEnabled: Record<string, string[]> = {};

  // set initialEnabled on first render when enabled is undefined
  let initEnabled: Record<string, string[]> | undefined;
  if (enabled === undefined) {
    newEnabled = getAvailableAttributeOptions(finalMatchedSkus, undefined, nearbyStores);
    initEnabled = { ...newEnabled };
  } else {
    initEnabled = initialEnabled;

    const allAttributeNames = new Set<string>();

    finalMatchedSkus.forEach((sku) => {
      sku.definingAttributes?.forEach((attr) => {
        allAttributeNames.add(attr.name);
      });
    });

    allAttributeNames.forEach((attrName) => {
      if (newSelected[attrName]) {
        // preserve initial enabled list for selected attributes (allows switching)
        if (initEnabled?.[attrName]) {
          newEnabled[attrName] = initEnabled[attrName];
        }
      } else {
        // calculate enabled from filtered SKUs for unselected attributes
        const optionsForThisAttr = getAvailableAttributeOptions(
          finalMatchedSkus,
          undefined,
          nearbyStores,
        );
        if (optionsForThisAttr[attrName]) {
          newEnabled[attrName] = optionsForThisAttr[attrName];
        }
      }
    });
  }

  // auto-select attributes with only one enabled option
  if (newEnabled && quickviewAutoSelectEnabled) {
    Object.keys(newEnabled).forEach((attribute) => {
      if (newEnabled[attribute].length === 1 && !newSelected[attribute]) {
        newSelected = {
          ...newSelected,
          [attribute]: newEnabled[attribute][0],
        };
      }
    });
  }

  return {
    skus,
    selected: newSelected,
    resolved: filterResolvedSku(skus, newSelected),
    enabled: newEnabled,
    initialEnabled: initEnabled,
  };
}

function reducer(state, action) {
  if (action.type === 'select') {
    const { attribute, value, quickViewproductNotAvailableImprovements } = action.payload;
    // If selection is already selected, do nothing
    if (state.selected && state.selected[attribute] === value) {
      return state;
    }

    const isEnabled = quickViewproductNotAvailableImprovements
      ? state.enabled[attribute]
      : state.enabled[attribute] && state.enabled[attribute].indexOf(value) > -1;

    let newSelected = null;
    // If it is enabled, add it to selections
    if (isEnabled) {
      newSelected = { ...state.selected, [attribute]: value };
      // If not enabled, clear existing selections and just use the desired one
    } else {
      newSelected = { [attribute]: value };
    }

    return init({
      skus: state.skus,
      selected: newSelected,
      resolved: state.resolved,
      enabled: state.enabled,
      justClicked: attribute,
      initialEnabled: state.initialEnabled,
      nearbyStores: state.nearbyStores,
    });
  }

  return state;
}

function useAttributeEngine(props: UseAttributeEngineProps) {
  const { style, skus } = props;
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const quickviewAutoSelectEnabled = useAppSelector(
    getFeatureFlagByNameSelector('quickviewAutoSelectEnabled'),
  );
  const quickViewproductNotAvailableImprovements = useAppSelector(
    getFeatureFlagByNameSelector('QVproductNotAvailableImprovements'),
  );
  // Get nearby stores
  const aggregatedSkuStoreList = useAppSelector(aggregatedStoreListSelector);

  // Set up intial selections
  let selected = {};

  if (style) {
    // look for attributes with only one value and select them
    style.definingAttributes.forEach((attr) => {
      const values = attr.value.split('|');
      if (values.length === 1) {
        selected = { ...selected, [attr.name]: attr.value };
      }
    });
  }

  const [state, dispatch] = useReducer(
    reducer,
    {
      skus,
      selected,
      resolved: skus,
      storeIdentifierName,
      quickviewAutoSelectEnabled,
      nearbyStores: aggregatedSkuStoreList,
    },
    init,
  );

  function handleClick(attribute: string, value: string) {
    dispatch({
      type: 'select',
      payload: { attribute, value, quickViewproductNotAvailableImprovements },
    });
  }

  return [state, handleClick];
}

export default useAttributeEngine;
